import React, { useState, useEffect, useCallback } from 'react';

const API = '/api/v1/google-review';

const fmtDt = (v) => {
  if (!v) return 'Never';
  try { return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '-'; }
};
const stars = (n) => {
  const r = Math.round(Number(n) || 0);
  return '★★★★★'.slice(0, r) + '☆☆☆☆☆'.slice(0, 5 - r);
};

function GoogleReview({ token, selectedProperty }) {
  const [cfg, setCfg] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({});
  const [lastFetched, setLastFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [form, setForm] = useState({ google_place_id: '', negative_threshold: 2, alerts_enabled: true });

  // Google Place finder — search by name+address, admin confirms which listing
  // is theirs. The confirmed place_id is what builds the guest review link.
  const [searchQuery, setSearchQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const reviewUrl = form.google_place_id.trim()
    ? `https://search.google.com/local/writereview?placeid=${encodeURIComponent(form.google_place_id.trim())}`
    : '';

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const [cRes, rRes] = await Promise.all([
        fetch(`${API}/config`, { headers: headers() }),
        fetch(`${API}/reviews`, { headers: headers() }),
      ]);
      const cData = await cRes.json();
      const rData = await rRes.json();
      if (!cRes.ok) throw new Error(cData.error || 'Failed to load config');
      if (!rRes.ok) throw new Error(rData.error || 'Failed to load reviews');
      setCfg(cData);
      setForm({
        google_place_id: cData.google_place_id || '',
        negative_threshold: cData.negative_threshold || 2,
        alerts_enabled: cData.alerts_enabled !== false,
      });
      setReviews(rData.reviews || []);
      setSummary(rData.summary || {});
      setLastFetched(rData.last_fetched_at);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty, headers]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true); setError(''); setMessage('');
    try {
      const res = await fetch(`${API}/sync`, { method: 'POST', headers: headers(), body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Sync failed');
      let msg = `Synced ${data.reviews_synced} review(s).`;
      if (data.baseline) msg += ' First sync — baseline stored (no alerts sent).';
      else if (data.new_negatives) msg += ` ${data.new_negatives} new negative review(s); ${data.alerts_sent} alert SMS sent.`;
      setMessage(msg);
      await load();
    } catch (err) { setError(err.message); }
    finally { setSyncing(false); }
  };

  const runPlaceSearch = async () => {
    setSearching(true); setError(''); setMessage('');
    try {
      const qs = searchQuery.trim() ? `?query=${encodeURIComponent(searchQuery.trim())}` : '';
      const res = await fetch(`${API}/place-search${qs}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      setCandidates(data.candidates || []);
      setSearched(true);
      // Show the admin what we actually searched for when they left it blank.
      if (!searchQuery.trim() && data.suggested_query) setSearchQuery(data.suggested_query);
    } catch (err) { setError(err.message); setCandidates([]); setSearched(true); }
    finally { setSearching(false); }
  };

  // Confirming a candidate saves it immediately — the admin has just told us
  // "this is my hotel", so making them also press Save is a step that only
  // creates a chance to lose the selection.
  const confirmPlace = async (candidate) => {
    setForm(f => ({ ...f, google_place_id: candidate.place_id }));
    setError(''); setMessage('');
    try {
      const res = await fetch(`${API}/config`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ google_place_id: candidate.place_id, place_name: candidate.name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setMessage(`Confirmed "${candidate.name}" as this hotel's Google listing.`);
      await load();
    } catch (err) { setError(err.message); }
  };

  const saveConfig = async () => {
    if (!form.google_place_id.trim()) { setError('Google Place ID is required'); return; }
    setSaving(true); setError(''); setMessage('');
    try {
      const res = await fetch(`${API}/config`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          google_place_id: form.google_place_id.trim(),
          negative_threshold: Number(form.negative_threshold),
          alerts_enabled: form.alerts_enabled,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setMessage('Configuration saved.');
      setShowConfig(false);
      await load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: '1rem', color: 'var(--theme-text-secondary)' }}>Select a property to view its Google reviews.</div>;
  }

  const canConfigure = cfg?.can_configure;
  const negThreshold = cfg?.negative_threshold || 2;

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <h2 style={{ margin: 0, color: 'var(--theme-primary)' }}>Google Review</h2>
        <span style={{ fontSize: '0.78rem', background: '#e0f2fe', color: '#0369a1', padding: '0.2rem 0.6rem', borderRadius: 4, fontWeight: 700 }}>
          {selectedProperty?.name || 'Property'}
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>Last synced: <b>{fmtDt(lastFetched)}</b></span>
        <button onClick={sync} disabled={syncing || !cfg?.google_place_id}
          title={!cfg?.google_place_id ? 'Set the Google Place ID first' : ''}
          style={{ padding: '0.45rem 1.1rem', background: (syncing || !cfg?.google_place_id) ? 'var(--theme-text-muted)' : 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 5, cursor: (syncing || !cfg?.google_place_id) ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
          {syncing ? 'Syncing…' : '⟳ Sync Now'}
        </button>
        {canConfigure && (
          <button onClick={() => setShowConfig(s => !s)} style={{ padding: '0.45rem 0.9rem', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            {showConfig ? 'Close' : '⚙ Configure'}
          </button>
        )}
      </div>
      <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
        Live Google reviews for this property. New reviews at or below {negThreshold}★ alert the hotel management numbers (Hotel Config → Alert Settings).
      </p>

      {error && <div style={{ background: '#fef2f2', color: 'var(--theme-danger)', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #fecaca' }}>{error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--theme-danger)', cursor: 'pointer', fontWeight: 700 }}>x</button></div>}
      {message && <div style={{ background: '#f0fdf4', color: 'var(--theme-success)', padding: '0.5rem 0.8rem', borderRadius: 6, marginBottom: '0.8rem', fontSize: '0.85rem', fontWeight: 600, border: '1px solid #bbf7d0' }}>{message} <button onClick={() => setMessage('')} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--theme-success)', cursor: 'pointer', fontWeight: 700 }}>x</button></div>}

      {/* Config panel */}
      {showConfig && canConfigure && (
        <div style={{ background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1rem 1.2rem', marginBottom: '1.2rem', maxWidth: 640 }}>
          <h4 style={{ margin: '0 0 0.8rem', color: 'var(--theme-primary-dark)' }}>Google Review Configuration</h4>
          <div style={{ display: 'grid', gap: '0.9rem' }}>
            {/* Place finder — the hotel admin should never hunt for a Place ID
                by hand. We search Google by the property's own name + address
                and let them confirm which listing is theirs. */}
            <div style={{ display: 'grid', gap: '0.5rem', background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '0.8rem 1rem' }}>
              <label style={lbl}>Find this hotel on Google</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') runPlaceSearch(); }}
                  placeholder="Hotel name, city — leave blank to use this property's name and address"
                  style={{ ...inp, flex: '1 1 260px' }}
                />
                <button onClick={runPlaceSearch} disabled={searching}
                        style={{ padding: '0.5rem 1.1rem', background: searching ? '#94a3b8' : 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  {searching ? 'Searching…' : '🔍 Search Google'}
                </button>
              </div>

              {searched && candidates.length === 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>
                  No matches. Try adding the city, or a landmark near the hotel.
                </div>
              )}

              {candidates.length > 0 && (
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
                    Pick the listing that is this hotel:
                  </div>
                  {candidates.map(c => {
                    const chosen = form.google_place_id === c.place_id;
                    return (
                      <div key={c.place_id}
                           style={{
                             display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap',
                             border: `1px solid ${chosen ? 'var(--theme-success)' : 'var(--theme-border)'}`,
                             background: chosen ? '#f0fdf4' : '#fff',
                             borderRadius: 6, padding: '0.6rem 0.8rem',
                           }}>
                        <div style={{ flex: '1 1 240px' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{c.name}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>{c.formatted_address}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                            {c.rating != null ? `${c.rating} ★ · ${c.user_ratings_total} reviews` : 'No rating yet'}
                            {c.business_status && c.business_status !== 'OPERATIONAL' ? ` · ${c.business_status}` : ''}
                          </div>
                        </div>
                        <button onClick={() => confirmPlace(c)}
                                style={{
                                  padding: '0.45rem 1rem', borderRadius: 5, border: 'none', cursor: 'pointer',
                                  fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap',
                                  background: chosen ? 'var(--theme-success)' : '#fff',
                                  color: chosen ? '#fff' : 'var(--theme-primary-dark)',
                                  boxShadow: chosen ? 'none' : 'inset 0 0 0 1px var(--theme-border)',
                                }}>
                          {chosen ? '✓ This is my hotel' : 'This is my hotel'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gap: '0.3rem' }}>
              <label style={lbl}>Google Place ID <span style={{ color: '#9ca3af', fontWeight: 400 }}>(this hotel's Google listing)</span></label>
              <input value={form.google_place_id} onChange={e => setForm({ ...form, google_place_id: e.target.value })} placeholder="ChIJ… (or use the search above)" style={inp} />
              <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>Uses the shared Google Places API key from Settings → Banquet Intelligence → API Configuration.</span>
              {cfg?.place_name && (
                <span style={{ fontSize: '0.75rem', color: 'var(--theme-success)', fontWeight: 600 }}>
                  Confirmed listing: {cfg.place_name}
                </span>
              )}
            </div>

            {/* The review link the guest receives — same id, derived not stored. */}
            {form.google_place_id.trim() && (
              <div style={{ display: 'grid', gap: '0.3rem', background: '#f8fafc', borderRadius: 8, padding: '0.7rem 0.9rem' }}>
                <label style={lbl}>Guest review link</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <code style={{ fontSize: '0.75rem', color: '#334155', wordBreak: 'break-all', flex: '1 1 240px' }}>
                    {reviewUrl}
                  </code>
                  <button onClick={() => { navigator.clipboard?.writeText(reviewUrl); setMessage('Review link copied.'); }}
                          style={{ padding: '0.35rem 0.8rem', background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 5, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                    Copy
                  </button>
                  <a href={reviewUrl} target="_blank" rel="noreferrer"
                     style={{ padding: '0.35rem 0.8rem', background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 5, fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none', color: 'var(--theme-primary-dark)' }}>
                    Open
                  </a>
                </div>
                <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                  This is what the guest gets in the WhatsApp review message. Open it once to confirm it lands on your hotel's review box.
                </span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'end' }}>
              <div style={{ display: 'grid', gap: '0.3rem' }}>
                <label style={lbl}>Negative threshold</label>
                <select value={form.negative_threshold} onChange={e => setForm({ ...form, negative_threshold: Number(e.target.value) })} style={{ ...inp, width: 180 }}>
                  <option value={1}>≤ 1★ (only 1-star)</option>
                  <option value={2}>≤ 2★ (1–2 star)</option>
                  <option value={3}>≤ 3★ (1–3 star)</option>
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', paddingBottom: '0.5rem' }}>
                <input type="checkbox" checked={form.alerts_enabled} onChange={e => setForm({ ...form, alerts_enabled: e.target.checked })} />
                Send SMS alert on new negative review
              </label>
            </div>
            <div>
              <button onClick={saveConfig} disabled={saving} style={{ padding: '0.5rem 1.4rem', background: 'var(--theme-success)', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                {saving ? 'Saving…' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary KPIs */}
      <div style={{ display: 'flex', gap: '0.7rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <Kpi label="Google Rating" value={summary.avg_rating != null ? `${Number(summary.avg_rating).toFixed(1)} ★` : '—'} color="#f59e0b" />
        <Kpi label="Total on Google" value={summary.google_review_count || 0} color="#0369a1" />
        <Kpi label="Fetched" value={summary.count || 0} color="#475569" />
        <Kpi label={`Negative (≤${negThreshold}★)`} value={summary.negative_count || 0} color="var(--theme-danger)" />
      </div>

      {loading && <div style={{ color: '#2563eb', marginBottom: '0.5rem' }}>Loading…</div>}

      {/* Reviews list */}
      <div style={{ display: 'grid', gap: '0.7rem' }}>
        {reviews.map(r => {
          const neg = Number(r.rating) <= negThreshold;
          return (
            <div key={r.id} style={{ background: '#fff', border: `1px solid ${neg ? '#fecaca' : 'var(--theme-border)'}`, borderLeft: `4px solid ${neg ? 'var(--theme-danger)' : '#f59e0b'}`, borderRadius: 8, padding: '0.8rem 1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700 }}>{r.reviewer_name || 'Anonymous'}</span>
                <span style={{ color: '#f59e0b', letterSpacing: 1 }}>{stars(r.rating)}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>{r.relative_time || (r.review_time ? fmtDt(r.review_time) : '')}</span>
                {neg && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', background: 'var(--theme-danger)', borderRadius: 4, padding: '0.1rem 0.4rem' }}>NEGATIVE</span>}
                {r.alerted_at && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '0.1rem 0.4rem' }}>ALERTED</span>}
              </div>
              {r.review_text && <p style={{ margin: '0.5rem 0 0', fontSize: '0.86rem', color: '#334155', lineHeight: 1.4 }}>{r.review_text}</p>}
            </div>
          );
        })}
        {!reviews.length && !loading && (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-secondary)', background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)' }}>
            {cfg?.google_place_id ? 'No reviews yet — click "Sync Now" to fetch from Google.' : 'Set the Google Place ID (⚙ Configure) then Sync to fetch reviews.'}
          </div>
        )}
      </div>
    </div>
  );
}

const lbl = { fontWeight: 600, fontSize: '0.8rem', color: 'var(--theme-text)' };
const inp = { padding: '0.55rem', borderRadius: 6, border: '1px solid #dcdde1', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };

function Kpi({ label, value, color }) {
  return (
    <div style={{ flex: 1, minWidth: 120, background: '#fff', borderRadius: 8, border: '1px solid var(--theme-border)', padding: '0.7rem 1rem', textAlign: 'center' }}>
      <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)', fontWeight: 600, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: color || '#1e293b' }}>{value}</div>
    </div>
  );
}

export default GoogleReview;
